import type { ReactNode } from 'react'

import Image from 'next/image'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { founders } from '@/constants/about'
import { siteConfig } from '@/constants/site'

export const AboutFounders = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-founders-eyebrow]'), {
            y: 16,
            opacity: 0,
            duration: 0.8,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 78%' }
        })

        gsap.from(select('[data-founders-title] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1.05,
            stagger: 0.05,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 78%' }
        })

        // Portraits slide up out of their frames, one after the other.
        select('[data-founder-card]').forEach((card, index) => {
            const trigger = { trigger: card, start: 'top 88%' }

            gsap.fromTo(
                card.querySelector('[data-founder-media]'),
                { clipPath: 'inset(0% 0% 100% 0%)' },
                {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.3,
                    delay: index * 0.12,
                    ease: 'nuvea',
                    scrollTrigger: trigger
                }
            )

            gsap.from(card.querySelector('[data-founder-media] img'), {
                scale: 1.2,
                duration: 1.7,
                delay: index * 0.12,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            gsap.from(card.querySelectorAll('[data-founder-meta]'), {
                y: 18,
                opacity: 0,
                duration: 0.8,
                stagger: 0.06,
                delay: index * 0.12 + 0.2,
                ease: 'nuvea',
                scrollTrigger: trigger
            })
        })

        gsap.from(select('[data-founders-note] > *'), {
            y: 26,
            opacity: 0,
            duration: 0.9,
            stagger: 0.08,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-founders-note]')[0],
                start: 'top 86%'
            }
        })
    })

    return (
        <section ref={scope} id='founders' className='shell-x mx-auto w-full max-w-[1760px]'>
            <div className='rounded-3xl bg-sage px-6 py-14 md:px-12 md:py-20 lg:px-16'>
                <p data-founders-eyebrow='' className='type-small inline-flex items-start text-ink'>
                    {siteConfig.wordmark}

                    <span className='mt-[0.15em] text-[0.55em] leading-none'>
                        &trade;
                    </span>
                </p>

                <SplitWords
                    data-founders-title=''
                    as='h2'
                    lines={['The two people', 'who set the rules.']}
                    className='type-h1 mt-5 max-w-[16ch] text-ink'
                />

                <ul className='mt-12 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-2 lg:gap-10'>
                    {founders.map((founder) => (
                        <li key={founder.name} data-founder-card=''>
                            <div className='flex items-baseline justify-between gap-4 pb-4'>
                                <p data-founder-meta='' className='type-micro text-ink/60'>
                                    {founder.role}
                                </p>

                                <p data-founder-meta='' className='type-body text-ink'>
                                    {founder.name}
                                </p>
                            </div>

                            <div
                                data-founder-media=''
                                className='relative aspect-[2/3] w-full overflow-hidden rounded-2xl bg-cream'
                            >
                                <Image
                                    src={founder.image}
                                    alt={founder.alt}
                                    fill
                                    sizes='(min-width: 768px) 45vw, 100vw'
                                    className='object-cover will-change-transform'
                                />
                            </div>
                        </li>
                    ))}
                </ul>

                <div className='mt-14 grid grid-cols-1 gap-8 md:mt-20 lg:grid-cols-[1fr_1.2fr] lg:gap-16'>
                    <div>
                        <p className='type-micro text-ink/60'>
                            Between them
                        </p>

                        <h3 className='type-h3 mt-4 max-w-[22ch] text-ink'>
                            Twenty years of clinical dermatology and nine years of barrier research, which is mostly
                            twenty years of watching good skin get worse.
                        </h3>
                    </div>

                    <div data-founders-note='' className='flex flex-col gap-5'>
                        {founders.map((founder) => (
                            <p key={founder.name} className='type-copy max-w-[58ch] text-ink/75'>
                                {founder.bio}
                            </p>
                        ))}

                        <p className='type-copy max-w-[58ch] text-ink/75'>
                            They wanted one product they could hand a patient without a set of instructions attached.
                            Three years and forty-one reformulations later, the list was
                            down to five and the trial results held.
                        </p>

                        <p className='type-copy max-w-[58ch] text-ink/75'>
                            Neither of them will sign off a claim we have not measured, which is why this site has fewer
                            numbers on it than most, and why the ones here have a sample
                            size attached.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}
